import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { deleteTeam, listTeams, TeamResponse } from '../../api/team';
import { Button } from '../../styles/common/Button.styled';
import * as T from '../../styles/common/table.styled';
import ConfirmModal from '../common/ConfirmModal';
import Pagination from '../common/Pagination';
import PageHeader from '../common/PageHeader';
import PageState from '../common/PageState';
import TeamModal from './TeamModal';
import { useAuth } from '../../contexts/AuthContext';
import { isTableRowActionTarget } from '../../utils/tableInteraction';
import DebouncedSearchInput from '../common/DebouncedSearchInput';

const PAGE_SIZE = 10;

const TeamList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mode } = useAuth();
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const requestSequenceRef = useRef(0);
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamResponse | null>(null);

  const currentPage = Number.parseInt(searchParams.get('pg') || '1', 10) || 1;
  const appliedSearchTerm = searchParams.get('q') || '';
  const [searchTerm, setSearchTerm] = useState(appliedSearchTerm);

  useEffect(() => {
    setSearchTerm(appliedSearchTerm);
  }, [appliedSearchTerm]);

  const fetchTeams = useCallback(async () => {
    const requestMode = modeRef.current;
    const requestSequence = ++requestSequenceRef.current;
    try {
      setIsLoading(true);
      setHasLoadError(false);
      const data = await listTeams({
        pg: currentPage,
        limit: PAGE_SIZE,
        displayName: appliedSearchTerm || undefined,
      });
      if (
        modeRef.current !== requestMode ||
        requestSequenceRef.current !== requestSequence
      ) return;
      setTeams(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      if (
        modeRef.current !== requestMode ||
        requestSequenceRef.current !== requestSequence
      ) return;
      setHasLoadError(true);
      toast.error('팀 목록을 불러오는데 실패했습니다.');
    } finally {
      if (
        modeRef.current === requestMode &&
        requestSequenceRef.current === requestSequence
      ) setIsLoading(false);
    }
  }, [appliedSearchTerm, currentPage]);

  useEffect(() => {
    if (mode) fetchTeams();
  }, [fetchTeams, mode]);

  const handleSearch = (nextSearchTerm?: string) => {
    const params: Record<string, string> = { pg: '1' };
    const query = typeof nextSearchTerm === 'string' ? nextSearchTerm : searchTerm;
    if (query) params.q = query;
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params: Record<string, string> = { pg: page.toString() };
    if (appliedSearchTerm) params.q = appliedSearchTerm;
    setSearchParams(params);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTeam) return;

    try {
      await deleteTeam(selectedTeam.id);
      toast.success('팀이 삭제되었습니다.');
      setIsConfirmOpen(false);
      setSelectedTeam(null);
      fetchTeams();
    } catch (error) {
      console.error('Failed to delete team:', error);
      toast.error('팀 삭제에 실패했습니다.');
    }
  };

  const handleModalSuccess = () => {
    setIsModalOpen(false);
    setSelectedTeam(null);
    fetchTeams();
  };

  return (
    <T.PageContainer>
      <PageHeader title="팀 관리" count={isLoading || hasLoadError ? undefined : totalElements} actions={<Button
          onClick={() => {
            setSelectedTeam(null);
            setIsModalOpen(true);
          }}
        >
          + 신규 팀 등록
        </Button>} />
      <T.TopBar>
        <T.SearchGroup>
          <T.SearchInput as={DebouncedSearchInput}
            aria-label="팀명 검색"
            placeholder="팀명 검색"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onSearch={handleSearch}
            delay={250}
          />
          <T.SearchButton onClick={() => handleSearch()}>검색</T.SearchButton>
        </T.SearchGroup>
      </T.TopBar>

      <T.TableArea $stickyActions $minWidth={480}>
        <T.Table>
          <colgroup>
            <col style={{ width: '60px' }} />
            <col />
            <col style={{ width: '160px' }} />
          </colgroup>
          <thead>
            <tr>
              <T.Th>No.</T.Th>
              <T.Th>팀명</T.Th>
              <T.Th>관리</T.Th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <T.Tr>
                <T.Td colSpan={3}><PageState status="loading" /></T.Td>
              </T.Tr>
            ) : hasLoadError ? (
              <T.Tr>
                <T.Td colSpan={3}>
                  <PageState status="error" title="팀 목록을 불러오는데 실패했습니다." onRetry={fetchTeams} />
                </T.Td>
              </T.Tr>
            ) : teams.length === 0 ? (
              <T.Tr>
                <T.Td colSpan={3}>
                  <PageState status="empty" title={appliedSearchTerm ? '검색 조건에 맞는 팀이 없습니다.' : '등록된 팀이 없습니다.'} />
                </T.Td>
              </T.Tr>
            ) : (
              teams.map((team, index) => (
                <T.Tr
                  key={team.id}
                  $clickable
                  onDoubleClick={(event) => {
                    if (isTableRowActionTarget(event.target)) return;
                    setSelectedTeam(team);
                    setIsModalOpen(true);
                  }}
                >
                  <T.Td>{totalElements - (currentPage - 1) * PAGE_SIZE - index}</T.Td>
                  <T.Td>{team.displayName}</T.Td>
                  <T.Td>
                    <T.ActionButton
                      style={{ marginRight: '6px' }}
                      onClick={() => {
                        setSelectedTeam(team);
                        setIsModalOpen(true);
                      }}
                    >
                      수정
                    </T.ActionButton>
                    <T.ActionButton
                      $variant="danger"
                      onClick={() => {
                        setSelectedTeam(team);
                        setIsConfirmOpen(true);
                      }}
                    >
                      삭제
                    </T.ActionButton>
                  </T.Td>
                </T.Tr>
              ))
            )}
          </tbody>
        </T.Table>
      </T.TableArea>

      {!isLoading && !hasLoadError && <T.TableFooter>
        <T.TotalCount aria-label="조회 결과">총 <span>{totalElements.toLocaleString()}</span>건</T.TotalCount>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </T.TableFooter>}

      {isModalOpen && (
        <TeamModal
          team={selectedTeam || undefined}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedTeam(null);
          }}
          onSuccess={handleModalSuccess}
        />
      )}
      {isConfirmOpen && (
        <ConfirmModal
          title="팀 삭제"
          message={`'${selectedTeam?.displayName}' 팀을 삭제하시겠습니까?\n삭제된 데이터는 복구할 수 없습니다.`}
          confirmLabel="삭제하기"
          cancelLabel="취소"
          onConfirm={handleConfirmDelete}
          onCancel={() => setIsConfirmOpen(false)}
        />
      )}
    </T.PageContainer>
  );
};

export default TeamList;
